import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Battery, Bolt, Clock, Gauge, Palette, Ruler, Weight } from "lucide-react";
import { useState } from "react";
import { formatPriceINR, getModel } from "@/lib/api";

export default function ModelDetail() {
  const { slug } = useParams();
  const { data: model, isLoading, error } = useQuery({
    queryKey: ["model", slug],
    queryFn: () => getModel(slug),
  });

  const [activeColor, setActiveColor] = useState(0);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-24" data-testid="model-detail-loading">
        <div className="animate-pulse h-[60vh] rounded-3xl bg-slate-100" />
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-24" data-testid="model-detail-not-found">
        <p className="font-heading text-2xl">Model not found.</p>
        <Link to="/models" className="mt-4 inline-flex items-center gap-2 text-emerald-700">
          <ArrowLeft className="h-4 w-4" /> Back to lineup
        </Link>
      </div>
    );
  }

  const specs = [
    { Icon: Gauge, label: "Top speed", value: `${model.top_speed_kmph} km/h` },
    { Icon: Battery, label: "Range", value: `${model.range_km} km` },
    { Icon: Bolt, label: "Battery", value: model.battery },
    { Icon: Clock, label: "Charging time", value: model.charging_time },
    { Icon: Ruler, label: "Motor", value: model.motor_power },
    { Icon: Weight, label: "Load Capacity", value: `${model.load_capacity_kg} kg` },
  ];

  return (
    <div data-testid={`model-detail-${model.slug}`} className="pb-24">
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16">
          <Link
            to="/models"
            data-testid="model-detail-back"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" /> All models
          </Link>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-5">
              <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">
                Model · {model.slug}
              </p>
              <h1
                data-testid="model-detail-name"
                className="mt-3 font-heading text-6xl sm:text-7xl tracking-tight font-light text-slate-950"
              >
                {model.name}
              </h1>
              <p className="mt-4 text-lg text-slate-600 max-w-md">{model.tagline}</p>

              <div className="mt-8 flex items-baseline gap-3">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                  Starting at
                </p>
                <p
                  data-testid="model-detail-price"
                  className="font-heading text-3xl text-slate-950"
                >
                  {formatPriceINR(model.price_inr)}
                </p>
              </div>

              <div className="mt-8">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2">
                  <Palette className="h-3 w-3" /> Available colors
                </p>
                <div className="mt-3 flex items-center gap-3">
                  {model.colors.map((c, i) => (
                    <button
                      key={c.name}
                      type="button"
                      data-testid={`model-color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setActiveColor(i)}
                      className={`group relative h-10 w-10 rounded-full border-2 transition-all ${
                        activeColor === i
                          ? "border-emerald-500 scale-110"
                          : "border-slate-200 hover:border-slate-400"
                      }`}
                      style={{ backgroundColor: c.hex }}
                      aria-label={c.name}
                    />
                  ))}
                </div>
                <p
                  data-testid="model-color-active"
                  className="mt-3 text-sm text-slate-600"
                >
                  {model.colors[activeColor].name}
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-slate-100 via-emerald-50/40 to-slate-100 aspect-[16/11] flex items-center justify-center p-4">
                <img
                  src={model.image}
                  alt={model.name}
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  className="max-h-full max-w-full object-contain"
                />
                <span className="absolute left-5 top-5 rounded-full bg-white/70 backdrop-blur-md border border-white/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-700">
                  {model.top_speed_kmph} km/h · License-free
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPECS BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-20" data-testid="model-detail-specs">
        <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">
          The numbers
        </p>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-slate-950">
          Specifications
        </h2>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {specs.map(({ Icon, label, value }) => (
            <div
              key={label}
              data-testid={`spec-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="rounded-2xl border border-slate-200 bg-white p-6"
            >
              <Icon className="h-5 w-5 text-emerald-600" />
              <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-slate-500">
                {label}
              </p>
              <p className="mt-1 font-heading text-xl text-slate-950">{value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DESCRIPTION + HIGHLIGHTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">
            About the {model.name}
          </p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-slate-950">
            Designed for the everyday.
          </h2>
          <p className="mt-6 text-slate-600 leading-relaxed text-lg font-light">
            {model.description}
          </p>
        </div>
        <div className="md:col-span-5">
          <div className="rounded-3xl bg-slate-950 text-white p-8">
            <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-400 font-semibold">
              Highlights
            </p>
            <ul className="mt-6 space-y-4">
              {model.highlights.map((h) => (
                <li
                  key={h}
                  data-testid={`highlight-${h.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
                  className="flex items-start gap-3 text-sm text-slate-200"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  {h}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-20">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 sm:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl sm:text-3xl text-slate-950">
              Want to see the {model.name} in person?
            </h3>
            <p className="mt-2 text-slate-600">
              Drop by our showroom or get in touch — we&apos;d love to walk you through it.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="model-detail-cta-contact"
            className="inline-flex items-center justify-center rounded-full bg-slate-950 text-white px-7 py-3.5 text-sm font-medium hover:bg-emerald-600 transition-colors"
          >
            Contact us
          </Link>
        </div>
      </section>
    </div>
  );
}
